import cn from 'utils/cn'
import useNavigation from 'core/hooks/navigation/useNavigation'
import Icon from 'core/components/icons/Icon'
import { createBrowserHistory } from 'history'
import CompanyToggle from 'core/widgets/CompanyToggle'

export interface BackNavLinkProps {
  /**
   * Destination path to navigate to when the link is clicked.
   */
  to: string
  /**
   * Additional class names for the navigation link container.
   */
  className?: string
  /**
   * If true, navigation will replace the current history entry.
   */
  shouldReplace?: boolean
  /**
   * If true, overrides browser history and always navigates to 'to'.
   */
  overrideHistory?: boolean
  /**
   * Optional state to pass along with the navigation, mirroring `useLocation().state`.
   */
  state?: Record<never, never>
}

const history = createBrowserHistory()

/**
 * BackNavLink component for navigation with back button behavior.
 * Supports custom navigation logic, history override, and styling.
 */
const BackNavLink = ({
  to,
  className,
  shouldReplace,
  overrideHistory = false,
  state
}: BackNavLinkProps) => {
  const { push, replace } = useNavigation()
  return (
    <div
      onClick={() => {
        if (window.history.length > 2 && !overrideHistory) {
          return history.back()
        }

        if (shouldReplace) {
          return replace(to, state)
        }
        push(to, state)
      }}
      role="navigation"
      data-title="back-button"
      className={cn([
        'cursor-pointer text-dark-4 text-md hover:text-primary whitespace-nowrap',
        className
      ])}
    >
      <CompanyToggle company="everlance">
        <Icon name="arrow-left" className="mr-1 mb-1" size={14} />
      </CompanyToggle>
      <CompanyToggle company="motus">
        <Icon name="chevron-left" className="mr-1 mb-1" size={14} />
      </CompanyToggle>
      Back
    </div>
  )
}

export default BackNavLink
